import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ColumnDef,
  PaginatedTable,
  PaginationProps,
} from '@cnx-saas/common-fe';
import { UserOutput } from '@cnx-saas/common';
import { useAuth } from '@cnx-saas/react-auth';
import { useEditFormDialog } from '../hooks/useEditUser';
import { OverflowMenu } from './OverflowMenu';

type TableContext = {
  readonly t: TFunction;
  onEdit(user: UserOutput): void;
};

type ExistingUserTableProps = {
  users: UserOutput[];
  pagination: PaginationProps;
  loading: boolean;
  refetch: () => void;
};
export const ExistingUserTable = ({
  users,
  pagination,
  loading,
  refetch,
}: ExistingUserTableProps) => {
  const { t } = useTranslation();

  const { user: authUser } = useAuth(true);

  const { openEditFormDialog } = useEditFormDialog(refetch);

  const onEdit = (user: UserOutput) => {
    openEditFormDialog(user);
  };

  const columns: ColumnDef<UserOutput, TableContext>[] = [
    {
      header: ({ t }) => t('userManagement.label.firstName', 'First Name'),
      render: (user) => `${user.firstName}`,
    },
    {
      header: ({ t }) => t('userManagement.label.lastName', 'Last Name'),
      render: (user) => `${user.lastName}`,
    },
    {
      header: ({ t }) => t('userManagement.label.email'),
      render: (user) => user.email,
    },
    {
      header: ({ t }) => t('userManagement.label.userType'),
      render: (user) => t(`userManagement.label.${user.userType}`),
    },
    {
      header: ({ t }) => t('userManagement.label.userStatus'),
      render: (user) => t(`userManagement.label.${user.status}`),
    },
    {
      header: () => '',
      render: (user, { onEdit }) =>
        authUser.email === user.email ? (
          ''
        ) : (
          <OverflowMenu user={user} onEdit={onEdit} />
        ),
      padding: 'inline',
      sx: { width: '48px' },
    },
  ];
  return (
    <PaginatedTable<UserOutput, never, TableContext>
      columns={columns}
      getRowKey={(row) => row.id}
      rows={users}
      context={{
        t,
        onEdit: onEdit,
      }}
      pagination={pagination}
      loading={loading}
      sx={{ gridArea: 'table' }}
    />
  );
};
